import {useState} from 'react'
import _ from 'lodash'
import {Accordion, Dimmer, Icon, Label, Loader} from 'semantic-ui-react'
import {
  IEvent,
  ILabel,
  ILabelingWidgetConfig,
  IUserLabelsMap,
  LabelingPanel,
} from '@/features/labeling'
import {JsonViewer} from '@/components/json-viewer'
import {RequirePermission} from '@/features/auth'
import {IDataSourceStats} from '@/features/data-sources'
import {IExpressionConfig} from '@/features/expressions'
import {ErrorMessage} from '@/features/errors'
import {IParsedMediaItem} from '@/utils/media'
import {EventFilters} from './filters'
import {PredictionsPanel} from './predictions'
import './style.css'

export interface ISidebarComponentsProps {
  predictionStats?: IDataSourceStats
  labelingWidgetConfig: ILabelingWidgetConfig<object>
  data?: IParsedMediaItem
  userLabels?: IUserLabelsMap
  selectedEvent?: IEvent
  currentTime?: number
  userRows?: string[]
  apiErrors?: string[]
  // isBusy?: boolean,
  isPredictionsLoading: boolean
  isLabelsLoading: boolean
  isReadOnly?: boolean
  channelTypeDescriptions?: {
    [key: string]: string
  }
  onRunPredictionFilterExpression: (expression: IExpressionConfig) => void
  onValueClick: (event: IEvent) => void
  findPredictionFromLabel: (label: ILabel<object>) => IEvent | undefined
  findLabelFromPrediction: (event: IEvent) => ILabel<object> | undefined
  selectRandomPrediction: () => void
}

export function SidebarComponents(props: ISidebarComponentsProps) {
  const {
    predictionStats,
    labelingWidgetConfig,
    data,
    userLabels,
    isPredictionsLoading,
    isLabelsLoading,
    isReadOnly,
    channelTypeDescriptions,
    selectedEvent,
    currentTime,
    userRows,
    apiErrors,
    onRunPredictionFilterExpression,
    onValueClick,
    // findPredictionFromLabel,
    findLabelFromPrediction,
    selectRandomPrediction,
  } = props

  const [activePanels, setActivePanels] = useState<string[]>(['predictions', 'labeling'])

  function handleOnTitleClick(id: string) {
    setActivePanels((prev) => {
      if (!_.includes(prev, id)) return _.concat(prev || [], [id])
      return _.without(prev || [], id)
    })
  }

  function handleOnEventClick(event: IEvent) {
    if (!onValueClick) return
    onValueClick(event)
  }

  return (
    <div className='sidebar-components'>
      <Accordion
        inverted
        fluid
        className='component-panels'
      >
        {/* <RequirePermission require='label.can_view_legacy_tools'>
                <Accordion.Title
                    active={_.includes(activePanels, 'export')}
                    onClick={() => handleOnTitleClick('export')}
                >
                    <Icon name='dropdown' /> export
                </Accordion.Title>
                <Accordion.Content active={_.includes(activePanels, 'export')}>
                    <ExportLabels />
                </Accordion.Content>
            </RequirePermission> */}

        <Accordion.Title
          active={_.includes(activePanels, 'filters')}
          onClick={() => handleOnTitleClick('filters')}
        >
          <Icon name='dropdown' /> filters
        </Accordion.Title>
        <Accordion.Content active={_.includes(activePanels, 'filters')}>
          <EventFilters
            predictionStats={predictionStats}
            onRunExpression={onRunPredictionFilterExpression}
          />
        </Accordion.Content>

        <Accordion.Title
          active={_.includes(activePanels, 'predictions')}
          onClick={() => handleOnTitleClick('predictions')}
        >
          <Icon name='dropdown' /> predictions
        </Accordion.Title>
        <Accordion.Content active={_.includes(activePanels, 'predictions')}>
          <div
            style={{
              position: 'relative',
              minHeight: '50px',
            }}
          >
            <PredictionsPanel
              data={data}
              userRows={userRows}
              userLabels={userLabels}
              selectedEvent={selectedEvent}
              setSelectedEvent={(event) => handleOnEventClick(event)}
              channelTypeDescriptions={channelTypeDescriptions}
            />
            <Dimmer active={isPredictionsLoading}>
              <Loader />
            </Dimmer>
          </div>
        </Accordion.Content>

        <RequirePermission require={'label.list_labels'}>
          <Accordion.Title
            active={_.includes(activePanels, 'labeling')}
            onClick={() => handleOnTitleClick('labeling')}
          >
            <Icon name='dropdown' /> labeling
          </Accordion.Title>
          <Accordion.Content active={_.includes(activePanels, 'labeling')}>
            <div
              style={{
                position: 'relative',
                minHeight: '50px',
              }}
            >
              <LabelingPanel
                readOnly={isReadOnly}
                predictionStats={predictionStats}
                labelingWidgetConfig={labelingWidgetConfig}
                userLabels={userLabels}
                data={data}
                currentTime={currentTime}
                selectedEvent={selectedEvent}
                setSelectedEvent={(event) => handleOnEventClick(event)}
                findLabelFromPrediction={findLabelFromPrediction}
                selectRandomPrediction={selectRandomPrediction}
              />
              <Dimmer active={isPredictionsLoading || isLabelsLoading}>
                <Loader />
              </Dimmer>
            </div>
          </Accordion.Content>
        </RequirePermission>

        {apiErrors ?
          <>
            <Accordion.Title
              active={_.includes(activePanels, 'errors')}
              onClick={() => handleOnTitleClick('errors')}
            >
              <Icon name='dropdown' />{' '}
              <Label
                color='red'
                content={`errors: ${apiErrors.length}`}
              />
            </Accordion.Title>
            <Accordion.Content active={_.includes(activePanels, 'errors')}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.25em',
                  padding: '0.25em',
                }}
              >
                {apiErrors.map((msg, i) => (
                  <ErrorMessage
                    key={`error-${i}-${msg}`}
                    message={msg}
                  />
                ))}
                <Dimmer active={isPredictionsLoading}>
                  <Loader />
                </Dimmer>
              </div>
            </Accordion.Content>
          </>
        : undefined}

        <Accordion.Title
          active={_.includes(activePanels, 'json')}
          onClick={() => handleOnTitleClick('json')}
        >
          <Icon name='dropdown' /> json
        </Accordion.Title>
        <Accordion.Content active={_.includes(activePanels, 'json')}>
          <JsonViewer
            expanded
            data={data}
          />
        </Accordion.Content>
      </Accordion>
    </div>
  )
}
